import { useCallback, useEffect, useRef, useState } from 'react';

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  cellText: string;
}

const initialState: ContextMenuState = {
  visible: false,
  x: 0,
  y: 0,
  cellText: '',
};

export const COPY_SUCCESS_FEEDBACK_DURATION_MS = 2000;

// Éléments sur lesquels le menu contextuel natif du navigateur doit primer sur le popup de copie
// (ex: liens, pour permettre "ouvrir dans un nouvel onglet"). Étendre ce sélecteur pour ajouter
// d'autres cas, ou poser data-context-menu-bypass sur un élément ponctuel.
const CONTEXT_MENU_BYPASS_SELECTOR = 'a[href], [role="link"], [data-context-menu-bypass]';

export const useCellContextMenu = () => {
  const [menuState, setMenuState] = useState<ContextMenuState>(initialState);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const copySuccessTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copySuccessTimerRef.current !== null) {
        clearTimeout(copySuccessTimerRef.current);
      }
    };
  }, []);

  const close = useCallback(() => {
    setMenuState(initialState);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
    // ← Ajout : si du texte est sélectionné, laisser le menu natif du navigateur agir
    if (window.getSelection()?.toString().length) return;

    const target = e.target as HTMLElement;

    // laisser le menu natif du navigateur agir sur les liens et éléments exemptés
    if (target.closest(CONTEXT_MENU_BYPASS_SELECTOR)) return;

    const cell = target.closest('td');
    if (!cell) return;

    const text = cell.textContent?.trim() ?? '';
    if (!text) return;

    e.preventDefault();
    setMenuState({ visible: true, x: e.clientX, y: e.clientY, cellText: text });
  }, []);

  const handleCopy = useCallback(async () => {
    if (!menuState.cellText) {
      return;
    }

    setCopyError(null);
    if (copySuccessTimerRef.current !== null) {
      window.clearTimeout(copySuccessTimerRef.current);
      copySuccessTimerRef.current = null;
    }
    setCopySuccess(false);

    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      setCopyError(
        "La fonction de copie dans le presse-papiers n'est pas prise en charge par ce navigateur.",
      );
      close();
      return;
    }

    try {
      await navigator.clipboard.writeText(menuState.cellText);
      setCopySuccess(true);
      copySuccessTimerRef.current = window.setTimeout(
        () => setCopySuccess(false),
        COPY_SUCCESS_FEEDBACK_DURATION_MS,
      );
      close();
    } catch {
      setCopyError("Une erreur s'est produite lors de la copie dans le presse-papiers.");
      close();
    }
  }, [menuState.cellText, close]);

  const dismissCopySuccess = useCallback(() => {
    if (copySuccessTimerRef.current !== null) {
      window.clearTimeout(copySuccessTimerRef.current);
      copySuccessTimerRef.current = null;
    }
    setCopySuccess(false);
  }, []);

  const dismissCopyError = useCallback(() => setCopyError(null), []);

  return {
    menuState,
    copySuccess,
    copyError,
    handleContextMenu,
    handleCopy,
    close,
    dismissCopySuccess,
    dismissCopyError,
  };
};
